import React from "react";
import {Modal, Button} from "antd";
import {connect} from "react-redux";
import {getTranslate, Translate, TranslateFunction} from "react-localize-redux";
import {history, Nullable} from "@data-force/app";
import {ExclamationCircleOutlined} from "@ant-design/icons";
import dayjs from "dayjs";
import StoreState from "../../interfaces/store/storeState";
import "./JobClaimDialog.scss";
import {
    ClaimJobPhaseAction,
    ClearJobListDataAction,
    DestroyConfirmClaimDialogAction,
    SetActiveJobTypeAction,
    SetJobFilterTypeAction,
} from "../../interfaces/store/actions/job";
import {
    claimJobPhase, clearJobListData, destroyConfirmClaimDialog, setActiveJobType, setJobFilterType,
} from "../../store/actions/job";
import {ClaimJobPhase, JobEntity, PhaseConfigNormalized} from "../../interfaces/store/actions/payload/job";
import HistoryParams from "../../interfaces/historyParams";
import {formatString, getHistoryParams, getOpeningUrl} from "../../utils/common";
import {Editors} from "../../interfaces/common";
import {JobFilterType, JobType} from "../../enums/enums";

interface StateProps {
    visible: boolean
    translate: TranslateFunction
    data: Nullable<JobEntity>
    phaseConfigList: PhaseConfigNormalized
    editors: Editors
    isMobile: boolean
}

interface DispatchProps {
    setActiveJobType(payload: string): SetActiveJobTypeAction

    setJobFilterType(payload: string): SetJobFilterTypeAction

    destroyConfirmClaimDialog() : DestroyConfirmClaimDialogAction

    claimJobPhase(payload: ClaimJobPhase): ClaimJobPhaseAction

    clearJobListData() : ClearJobListDataAction
}

type Props = StateProps & DispatchProps;

class JobClaimDialog extends React.Component<Props> {
    private static readonly translationPath: string = "components.JobClaimDialog.JobClaimDialog";

    constructor(props: Props) {
        super(props);

        this.claimHandler = this.claimHandler.bind(this);

        this.cancelHandler = this.cancelHandler.bind(this);
    }

    /**
     * Content for claim dialog witch are able to change depending on:
     * - Whether it is displayed on a mobile device or desktop
     * - Does the job have a time limit and is partial job completion supported
     * @return React.ReactNode
     */
    getDialogContent(): React.ReactNode {
        const {translate, isMobile, data}: Props = this.props;
        const {translationPath}: typeof JobClaimDialog = JobClaimDialog;

        const mobileWarningTxt: React.ReactNode = <Translate id={`${translationPath}.mobileWarningTxt.text`}/>;
        let warningContent: React.ReactNode = <p>{mobileWarningTxt}</p>;
        let timeLimitWarningTxt: string = "";

        if (data?.timeLimit) {
            const minutes: string = dayjs.duration(data.timeLimit * 1000).asMinutes().toString();
            if (data.allowedSubmission) {
                timeLimitWarningTxt = formatString(translate(`${translationPath}.allowedSubmissionWarningTxt.text`).toString(), minutes);
            } else {
                timeLimitWarningTxt = formatString(translate(`${translationPath}.expiryWarningTxt.text`).toString(), minutes);
            }
            warningContent = <p>{timeLimitWarningTxt}</p>;

            if (isMobile) {
                warningContent = (
                    <ul>
                        <li>{mobileWarningTxt}</li>
                        <li>{timeLimitWarningTxt}</li>
                    </ul>
                );
            }
        }

        return (
            <>
                <Translate id={`${translationPath}.descriptionTxtPartOne.text`}/>
                {!data?.timeLimit && <Translate id={`${translationPath}.descriptionTxtPartTwo.text`}/>}
                {(isMobile || !!data?.timeLimit) && (
                    <div className="df-claim-warning">
                        <Translate id={`${translationPath}.claimWarningTitle.text`}/>
                        {warningContent}
                    </div>
                )}
            </>
        );
    }

    /**
     * Claim button handler, claims selected job phase, redirects to in progress job list,
     * if openEditor set to true allows for direct opening of the job
     * @param {boolean} openEditor
     */
    claimHandler(openEditor: boolean): void {
        const {name: phaseName, projectId, jobId}: JobEntity = this.props.data as JobEntity;

        this.props.claimJobPhase({
            projectId,
            jobId,
            phaseName,
            openEditor,
        });

        this.props.destroyConfirmClaimDialog();
        this.props.clearJobListData();
        this.props.setJobFilterType(JobFilterType.ONGOING);
        this.props.setActiveJobType(JobType.IN_PROGRESS);

        const historyParams: Nullable<HistoryParams> = getHistoryParams();
        history.push(`/${historyParams?.idpId}/jobs`);
    }

    /**
     * Cancel button handler, closes the modal.
     */
    cancelHandler(): void {
        this.props.destroyConfirmClaimDialog();
    }

    render(): React.ReactNode {
        const {
            visible, editors, phaseConfigList, data,
        }: Props = this.props;
        const {translationPath}: typeof JobClaimDialog = JobClaimDialog;
        const url: string = getOpeningUrl(editors, phaseConfigList, data!);

        return (
            <Modal
                className="df-modal-claim"
                title={(
                    <span>
                        <ExclamationCircleOutlined aria-hidden/>
                        <Translate id={`${translationPath}.title.text`}/>
                    </span>
                )}
                destroyOnClose
                closable={false}
                visible={visible}
                centered
                onCancel={this.cancelHandler}
                footer={[
                    <Button
                        key="cancel"
                        onClick={this.cancelHandler}
                    >
                        <Translate id={`${translationPath}.cancel.text`}/>
                    </Button>,
                    <Button
                        key="claim"
                        className="df-modal-claim-affirmative"
                        type="primary"
                        onClick={this.claimHandler.bind(this, false)}
                        hidden={!!data?.timeLimit}
                    >
                        <Translate id={`${translationPath}.claim.text`}/>
                    </Button>,
                    <Button
                        type="primary"
                        className="df-modal-claim-affirmative"
                        key="open"
                        disabled={!url}
                        onClick={this.claimHandler.bind(this, true)}
                    >
                        <Translate id={`${translationPath}.claimAndOpen.text`}/>
                    </Button>,
                ]}
            >
                {this.getDialogContent()}
            </Modal>
        );
    }
}

function mapStateToProps(state: StoreState): StateProps {
    return {
        visible: state.job.isConfirmClaimVisible,
        translate: getTranslate(state.localize),
        data: state.job.selectedJob,
        phaseConfigList: state.job.phaseConfigList,
        editors: state.serviceProperties.editors,
        isMobile: state.mobile.isMobile,
    };
}

const mapDispatchToProps: DispatchProps = {
    setActiveJobType,
    setJobFilterType,
    claimJobPhase,
    clearJobListData,
    destroyConfirmClaimDialog,
};

export default connect(mapStateToProps, mapDispatchToProps)(JobClaimDialog);
